type ApiUrlID =
  | 'azureAPI'
  | 'sparta'
  | 'spartaAPI'
  | 'safetyAPI'
  | 'viewpointAPI'
  | 'packetAPI'
  | 'securityAPI'
  | 'softwareSecurityAPI'
  | 'fileManagementAPI'
  | 'tasksAPI'
  | 'ratesAPI'
  | 'reportsServer'
  | 'dynamicAPI'
  | 'toolTrackerAPI'
  | 'myWeb'
  | 'warehouseWeb'
  | 'spartaWeb'
  | 'appStoreWeb'
  | 'resourceAllocationWeb'
  | 'hseToolboxWeb'
  | 'bridgeAPI'
  | 'itPortalAPI';

export type ApiMap = {
  [key in ApiUrlID]?: string;
};

export interface IEnvironmentConfiguration {
  production?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  environment_name?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  api_url?: string;
  baseRoutePath?: string;
  absorb?: {
    onboardingDepartmentId?: string;
  };
  appsync?: {
    production?: boolean;
    android?: {
      productionKey?: string;
      stagingKey?: string;
    };
    ios?: {
      productionKey?: string;
      stagingKey?: string;
    };
    version?: string;
    serverUrl?: string;
  };
  /**
   * @deprecated SmartLook SDK removed (#30070). Kept optional so old merges do
   * not break typing; do not set keys — they are ignored.
   */
  smartlook?: {
    webApiKey?: string;
    mobileApiKey?: string;
    allowedUrlsForPayloadRecord?: string;
  };
  // Ryn Replay (in-house session recorder). Fail-closed: an app with no
  // `rynReplay` block, or with `enabled` falsy, records nothing and loads
  // nothing. `allowedUrlsForPayloadRecord` is a regex source string.
  rynReplay?: {
    enabled?: boolean;
    allowedUrlsForPayloadRecord?: string;
    /**
     * Comma list of hosts payloads may come from (`*.x.com` = any subdomain,
     * otherwise exact). Unset keeps the recorder's owned-host default.
     */
    allowedHostsForPayloadRecord?: string;
    maskAllInputs?: boolean;
    /**
     * Mask every text node in the page, not just typed input values (PR 3639
     * review). Off by default: only an app whose pages routinely render raw
     * PII/export-error text outside a form control (web-hr-packet) opts in per
     * app. `maskAllInputs` alone never covered rendered text, only what a user
     * types into a field.
     */
    maskAllText?: boolean;
    /**
     * Also redact opaque-looking URL path segments (JWT, random tokens,
     * base64url) on every recorded URL, WI 34328. Off by default; GUIDs,
     * numeric ids, slugs and filenames survive. The known credential-route
     * markers apply whether or not this is set.
     */
    redactOpaquePathSegments?: boolean;
    /**
     * CSS class marking a subtree as never-record. Pinned: the recorder
     * always uses `ryn-replay-block` regardless of this value (PR 3639
     * review). Every template in the codebase hard-codes that literal class
     * name, so letting config repoint it would silently stop every existing
     * `ryn-replay-block` wrapper from blocking anything. Reserved, not read.
     */
    blockClass?: string;
    /**
     * Extra CSS selector(s) to block in ADDITION to the recorder's own
     * baseline (`kendo-numerictextbox`, PR 3639 review), comma-joined for
     * more than one. Set it in an app's environment file; there is no App
     * Configuration key for it (a non-optional key absent from App Config
     * fails every build). It can only ADD to the baseline, never replace it.
     */
    extraBlockSelector?: string;
    /**
     * Absolute host the replay player is served from, e.g.
     * `https://replay.universalplant.com`. Omit to keep replay links on the
     * app's own origin. `sessionUrl()` is always absolute either way, because
     * replay links are read from Halo ITSM tickets and email, outside the app.
     */
    playerBaseUrl?: string;
    /**
     * Absolute ingest endpoint, e.g. `https://fn-ryn-replay-upsllc.azurewebsites.net/api/ingest`.
     * Capture stays off without it: the recorder will not buffer a session it
     * has nowhere to send.
     */
    ingestUrl?: string;
    /**
     * Per-app shared ingest key, travelling in the `X-Ryn-Replay-Key` header.
     * The server resolves the writing app FROM this key and rejects any batch
     * whose `app` field disagrees, so the key is the authority and `app` below
     * is only a cross-check.
     */
    ingestKey?: string;
    /**
     * App token this key is registered to, e.g. `web-sparta`. Must match the
     * key's registration (`ReplayEnvelopeValidator` rejects a mismatch) and must
     * be 1-60 chars of `[A-Za-z0-9._-]` because it becomes a blob path segment.
     */
    app?: string;
    /**
     * Absolute base of the gated READ API the player reads sessions from, e.g.
     * `https://fn-ryn-replay-upsllc.azurewebsites.net/api`. Same function app as
     * `ingestUrl` but a separate setting on purpose: ingest is authenticated by
     * a per-app shared key and read is authenticated by the viewer's Entra
     * token, so an app can legitimately record without hosting the player, and
     * an app can host the player without recording anything itself.
     */
    readApiUrl?: string;
    /** Flush after this many events. Clamped to the server's 20,000 cap. */
    maxBatchEvents?: number;
    /** Flush after this many serialized bytes. Clamped to the server's 15 MB cap. */
    maxBatchBytes?: number;
    /** Time-based flush, so a quiet session still reaches the server. */
    flushIntervalMs?: number;
    /** iOS masked screenshot trial. Requires the native pod; disabled in production. */
    mobileScreenshotTrial?: boolean;
    /** Screenshot privacy mode. `blocked-only` is intended for controlled STG trials. */
    mobileScreenshotMasking?: 'strict' | 'blocked-only';
    /**
     * Override library idle segmentation (default 30 min). `0` disables.
     */
    idleTimeoutMs?: number;
    /**
     * Override library max-duration segmentation (default 8 h). `0` disables.
     */
    maxSessionDurationMs?: number;
    /**
     * CSS selectors for heavy elements where continuous capture is paused while
     * keyboard focus is inside them, so a dense inline grid is not serialized on
     * the browser main thread (#31398: the Equipment Usage grid froze under the
     * per-keystroke DOM mutation storm the recorder was serializing). The
     * recorder pauses on focusin within any matching element (e.g. the page host
     * `ups-equipment-usage-page`) and resumes on focusout once focus has left
     * every matching element, re-emitting a fresh FullSnapshot. Undefined or
     * empty means never pause on focus.
     */
    pauseOnFocusSelectors?: string[];
    /**
     * CSS selectors for heavy page hosts where capture is paused for the whole
     * time any matching element is mounted in the document (#31398). Stronger
     * than `pauseOnFocusSelectors`: focus pause misses the initial grid render
     * (and any off-focus Kendo churn) because it only fires after a cell is
     * focused. Presence pause detaches rrweb as soon as e.g.
     * `ups-equipment-usage-page` appears and resumes when it is gone, so the
     * visit is a short gap in the replay rather than a main-thread freeze.
     * Undefined or empty means never pause on presence. Composes with focus and
     * mutation-burst guards via the shared auto-pause reason set.
     */
    pauseWhilePresentSelectors?: string[];
    /**
     * Centralized rapid-DOM-mutation-burst auto-pause (#31398 follow-up). The
     * recorder watches the global DOM mutation RATE (counting, never
     * serializing) and pauses capture during a storm on ANY heavy page, then
     * resumes when it settles, with no per-page selector list. This is the
     * page-agnostic successor to `pauseOnFocusSelectors`.
     *
     * Default ON for every replay-enabled app: leave this undefined to get the
     * benchmarked defaults, set `enabled: false` as the kill switch. Thresholds
     * default to the stg Equipment Usage benchmark (storm median 532 / peak 4308
     * mutations per 300ms window; pause at 300 only after that rate has held
     * for 1s, so Angular+Kendo first paint does not false-pause; resume after
     * the rate stays near-idle ~800ms). Override only to tune.
     */
    mutationBurstPause?: {
      enabled?: boolean;
      /** Sliding window, in ms, whose summed mutation count is the rate. */
      windowMs?: number;
      /** Pause once the windowed sum reaches this many mutations. */
      burstThreshold?: number;
      /** The window is considered quiet once its sum is at or below this. */
      settleThreshold?: number;
      /** Resume only after the window has stayed quiet for this many ms. */
      settleMs?: number;
      /**
       * Pause only after the windowed sum has stayed at or above
       * `burstThreshold` for this many ms. Skips a one-window first-paint
       * spike. `0` pauses on the first crossing.
       */
      sustainMs?: number;
      /**
       * Pause IMMEDIATELY, bypassing `sustainMs`, once a single window reaches
       * this many mutations. A window this large is never a benign first paint
       * (peak ~4308); it is a catastrophic storm that freezes the tab within the
       * window itself (Equipment Usage measured 62,644 in one 300ms window).
       * Defaults to 8000. `0` disables the immediate path.
       */
      hardCeiling?: number;
      /**
       * Minimum time to stay paused once paused, on top of the `settleMs` quiet
       * requirement, before capture resumes and re-emits a full DOM snapshot.
       * During editing the rate oscillates, so a low resume threshold cycles
       * pause/resume and re-snapshots the heavy grid on each resume. Holding the
       * pause for at least this long collapses a burst of edit-session storms
       * into one pause. Defaults to 2500. `0` disables the floor.
       */
      minPauseMs?: number;
    };
  };
  microsoftClarity?: {
    projectId?: string;
  };
  apiHeader?: string;
  sockets?: {
    signalR?: {
      url?: string;
    };
  };
  liveUpdate?: {
    url?: string;
  };
  featuresSwitch?: {
    enableSignalR?: boolean;
  };
  // shorthand identifier name for app
  name?: string;
  // default home route for the app
  homeRoute?: string;
  // longer full display name for app (often used in UI displays)
  displayName?: string;
  sidebarName?: string;
  // Stable, code-defined app slug (e.g. 'sparta') used to prefix in-app help/nav
  // anchors as `<appSlug>-<page>`. Must be globally unique across apps and is
  // NOT the localized display name, so walkthrough anchors stay stable across
  // renames and unique even when two apps share a page name (e.g. "Admin").
  appSlug?: string;
  helpDocumentsDirName?: string;
  version?: string;
  auth0?: {
    domain?: string;
    audience?: string;
    callbackPath?: string;
    clientId?: string;
    useRefreshTokens?: boolean;
    /** Auth0 token cache for deployed origins. Pair 'localstorage' with
     * useRefreshTokens so the refresh token survives a reload (Bug 34695). */
    cacheLocation?: 'memory' | 'localstorage';
  };
  entraId?: {
    audience?: string;
    authority?: string;
    instance?: string;
    domain?: string;
    tenantId?: string;
    clientId?: string;
    /**
     * Custom-scheme redirect URI for the NativeScript interactive (system-
     * browser) AD sign-in, e.g. `com.universalplant.rynsroost://callback`.
     * Web apps use `authority`/MSAL redirect handling and leave this unset.
     */
    redirectUri?: string;
  };
  // Ryn bridge host-registration behavior (Ryn's Roost / Control Center web).
  rynBridge?: {
    /**
     * Break-glass switch for the legacy PSK browser-approval fallback.
     *
     * DEFAULT (undefined / false) = OFF. Entra ID + Software Security is the
     * sole bridge-auth path: when it fails, RoostBridgeAuthService surfaces a
     * clear, actionable error (consent-required / SS-not-granted / network /
     * config) instead of silently dropping to the fragile Adaptive-Card PSK
     * flow that dead-ended users when browser storage was cleared (WI 31554).
     *
     * Set to true ONLY to temporarily restore the manual-approval path during
     * an Entra/SS outage. Note the approval card is not currently delivered to
     * Teams, so break-glass is non-functional until that is fixed separately.
     */
    allowPskFallback?: boolean;
  };
  security?: {
    allowEverything?: boolean;
    securityOverride?: boolean;
    securityOverrideJson?: string;
    apiUrl?: string;
    appId?: string;
  };
  // Ask Ryn availability gate. When an app sets `featureUniqName`, the docked
  // Ask Ryn launcher + chat only appear for users whose security profile grants
  // ViewFeature on that feature. Apps that leave it unset get NO launcher —
  // fail-closed both ways (see shouldExposeAskRyn; the earlier fail-open default
  // exposed the in-dev button to all users). Lets a release be limited to a
  // small pilot group via Software Security role assignment, without touching
  // the shared Ask Ryn lib.
  askRyn?: {
    featureUniqName?: string;
  };
  // IT Portal (WI 35154): the delegated scope its own Entra API registration
  // exposes, which fn-it-portal validates. MSAL requests it for calls to
  // urls.itPortalAPI only; every other https call keeps the shared audience.
  itPortal?: {
    apiScope?: string;
  };
  syncfusionLicenseKey?: string;
  urls?: ApiMap;
  ttsApiKey?: string;
  nxCloud?: {
    rToken?: string;
    rwToken?: string;
  };
  logging?: {
    defaultLogLevel: number;
  };
  offline?: {
    enabled?: boolean;
    showManager?: boolean;
  };
  mobile?: {
    appId?: string;
    auth401Redirect?: string;
    appStoreLink?: {
      ios?: string;
      android?: string;
    };
    landingPage?: string;
  };
  host?: string;
  blobStorage?: {
    url?: string;
    container?: string;
  };
  customerForm?: string;
  haloITSMCategory?: string;
  haloITSMCategories?: string;
}
